<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cleanAmountInput, DecimalInput, stripPendingSeparator } from '@taler/ui'
import {
  type CurrencyCode,
  displayDigitsFor,
  equalsMoney,
  formatMoney,
  type Locale,
  type Money,
  money,
  numberFormatFor,
  openingAmount,
} from '@taler/domain'

/**
 * A currency-shaped text input: grouped as you type with the caret held on the digit it
 * started next to, a decimal point capped at the currency's own display digits, and a
 * currency badge inside the field. Takes and emits a `Money | null` - never a string, never
 * decimals/separators/a unit label a caller would otherwise have to assemble by hand, since
 * all of that is derivable from the amount's own currency plus `locale`. `null` is "no value" -
 * a cleared field or a currency not chosen yet - never a zero-unit `Money`, because a target
 * with no currency selected has no currency to build one from, and conflating "nothing typed"
 * with "the user typed zero" would lose information a required-field check needs.
 *
 * `currency` is always the caller's own live selection - it governs decimals and the unit badge
 * whether or not a value exists yet, and a caller switching it after typing an amount reinterprets
 * the same digits under the new currency rather than silently keeping the old one's precision.
 *
 * Lives in `apps/web`, not `packages/ui`: the design system may not depend on `@taler/domain`
 * (packages/ui's own layering rule), but this component's whole job is to know a `Money`, so it
 * has to sit one layer up. The actual grouped/caret-safe text input is `@taler/ui`'s
 * `DecimalInput` (plain strings, no domain type) - this component only converts `Money` to and
 * from the clean string that wraps, the same composition a non-money numeric field (an
 * interest rate, a target's ratio bound, a cross-currency filter magnitude) uses `DecimalInput`
 * for directly, without ever going through a `Money` that does not apply to it.
 */
const props = withDefaults(
  defineProps<{
    modelValue: Money | null
    /** The currency this field is denominated in - required even when `modelValue` is `null`,
     * since there is otherwise nothing to derive decimals or a unit label from. */
    currency: CurrencyCode | undefined
    locale: Locale
    allowNegative?: boolean
    /** A live formatted echo below the field - off for a dense grid/table row. */
    showTotal?: boolean
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty id is a real attribute that can collide
    id?: string | undefined
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty aria-label suppresses the accessible name
    ariaLabel?: string | undefined
    required?: boolean
    disabled?: boolean
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; an empty placeholder is a real attribute
    placeholder?: string | undefined
  }>(),
  { showTotal: true, allowNegative: false, required: false, disabled: false },
)

const emit = defineEmits<{ 'update:modelValue': [value: Money | null] }>()

defineOptions({ inheritAttrs: false })

/** The caller's own live currency selection always governs display and parsing - it is the
 * caller's current intent, not a fact `modelValue` gets to override once set. */
const currency = computed(() => props.currency)
const decimalDigits = computed(() => (currency.value === undefined ? 2 : displayDigitsFor(currency.value)))
const numberFormat = computed(() => numberFormatFor(props.locale))

/** The exact amount, trimmed to the currency's own display digits but never rounded below
 * them - opening the field on an existing value must never change what it reads until the
 * user actually touches it, and must not pad it with wire-scale zeros the user never typed.
 *
 * Kept as its own local ref, not a computed over `modelValue`, specifically so the same typed
 * digits survive a `currency` change in either direction: a caller commonly shows the currency
 * picker and the amount field side by side with no fixed fill order (LoanCreateForm.vue,
 * ForecastVariableForm.vue), and re-deriving from `clean` on every `currency` change - not only
 * on a keystroke - is what makes typing "500" then picking EUR work the same as picking EUR
 * then typing "500". It is also what makes picking a genuinely different currency after a value
 * already exists *reinterpret* the same digits under the new one rather than silently wiping
 * them: the number a user already typed is what they meant, relabelled, not discarded.
 */
let lastEmitted = props.modelValue
const clean = ref(props.modelValue === null ? '' : openingAmount(props.modelValue))

/** Reference equality breaks here: assigning a Money into a `ref` lets Vue wrap it in a
 * reactive proxy, so the value read back through `modelValue` is never `===` the object this
 * component itself just emitted - every v-model round trip would otherwise look like an
 * external change and reformat `clean` to wire scale on every keystroke. */
function sameMoney(a: Money | null, b: Money | null): boolean {
  if (a === null || b === null) return a === b
  return equalsMoney(a, b)
}

watch(
  () => props.modelValue,
  (value) => {
    if (!sameMoney(value, lastEmitted)) clean.value = value === null ? '' : openingAmount(value)
  },
)

watch(currency, () => {
  // Re-cap the typed digits to the new currency's own decimals, so a HUF field never keeps the
  // EUR fraction it was reinterpreted from ("500.00" -> "500").
  clean.value = stripPendingSeparator(
    cleanAmountInput(
      clean.value,
      {
        groupSeparator: numberFormat.value.groupSeparator,
        decimalSeparator: numberFormat.value.decimalSeparator,
        decimalDigits: decimalDigits.value,
        allowNegative: props.allowNegative,
      },
      false,
    ),
  )
  emitFromClean()
})

function onUpdateClean(next: string): void {
  clean.value = next
  emitFromClean()
}

function emitFromClean(): void {
  const text = clean.value
  const value = text === '' || text === '-' || currency.value === undefined ? null : tryMoney(text)
  lastEmitted = value
  emit('update:modelValue', value)
}

function tryMoney(text: string): Money | null {
  try {
    return money(text, currency.value!)
  } catch {
    return null
  }
}

const echo = computed(() => {
  if (props.modelValue === null) return undefined
  return formatMoney(props.modelValue, { locale: props.locale })
})
</script>

<template>
  <div class="flex flex-col gap-1">
    <DecimalInput
      v-bind="$attrs"
      :model-value="clean"
      :decimal-digits="decimalDigits"
      :group-separator="numberFormat.groupSeparator"
      :decimal-separator="numberFormat.decimalSeparator"
      :unit="currency"
      :allow-negative="allowNegative"
      :show-total="false"
      :id="id"
      :aria-label="ariaLabel"
      :required="required"
      :disabled="disabled"
      :placeholder="placeholder"
      @update:model-value="onUpdateClean"
    />
    <p
      v-if="showTotal && echo !== undefined"
      aria-hidden="true"
      class="text-[length:var(--text-step-1)] tabular-nums text-[color:var(--color-text-muted)]"
      :style="{ fontFamily: 'var(--font-numeric)' }"
    >
      {{ echo }}
    </p>
  </div>
</template>
